import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal } from '../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalHeader } from '../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalFooter } from '../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import {
  accountPortfolioDataToForm,
  dataToForm,
  FormFields,
  getShowAssetAllocationTab,
  INSURANCE_TYPES_WITH_BENEFICIARIES_TAB,
  UPSERT_INSURANCE_FORM_ID,
  validation,
} from './UpsertInsuranceModal.meta';
import { InsuranceAccountDTO, InsuranceType } from '../../api/useInsuranceQuery';
import { LifeInsuranceForm } from '../../forms/LifeInsuranceForm/LifeInsuranceForm';
import { DisabilityInsuranceForm } from '../../forms/DisabilityInsuranceForm/DisabilityInsuranceForm';
import { LongTermCareForm } from '../../forms/LongTermCareForm/LongTermCareForm';
import { HealthInsuranceForm } from '../../forms/HealthInsuranceForm/HealthInsuranceForm';
import { InsuranceToDelete, InsuranceToUpsert } from '../../Insurance.meta';
import { useUpsertInsuranceMutation } from '../../api/useUpsertInsuranceMutation';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { useAccountBeneficiariesMutation } from '../../../../Profile/Accounts/api/useAccountBeneficiariesMutation';
import { queryKeys } from '../../api/queryKeys';
import {
  AccountPortfolioDTO,
  useAccountPortfolioQuery,
} from '../../../../Profile/Accounts/api/useAccountPortfolioQuery';
import { MESSAGES } from '../../../../CommonConstants';
import { FullPageLoading } from '../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { InsuranceData } from '../../useInsurance';
import { useAccountPortfolioMutation } from '../../../../Profile/Accounts/api/useAccountPortfolioMutation';
import { ACCOUNTS_QUERY_KEY } from '../../../../Profile/Accounts/AccountsConstants';
import {
  ACCOUNT_OPTIONS_QUERY_KEY,
  AccountType,
} from '../../../../Profile/Accounts/api/useAccountsOptionsQuery';
import { PropertyAndCasualtyForm } from '../../forms/PropertyAndCasualtyForm/PropertyAndCasualtyForm';

const FormsDict = {
  [InsuranceType.Life]: LifeInsuranceForm,
  [InsuranceType.Disability]: DisabilityInsuranceForm,
  [InsuranceType.LongTermCare]: LongTermCareForm,
  [InsuranceType.HealthInsurance]: HealthInsuranceForm,
  [InsuranceType.PropertyAndCasualty]: PropertyAndCasualtyForm,
};

export interface Props {
  clientId: string;
  insurance: InsuranceToUpsert;
  data: InsuranceData;
  onInsuranceTypeChange(insuranceType: InsuranceType): void;
  onClose(): void;
  insuranceToDelete: InsuranceToDelete | null;
  setInsuranceToDelete(insuranceToDelete: InsuranceToDelete | null): void;
  isEstatePlanningWorkflow: boolean;
}

export const UpsertInsuranceModal = (props: Props) => {
  const { insurance } = props;

  if (insurance.AccountId && insurance.InsuranceType === InsuranceType.Life) {
    return <InsuranceWithAssetAllocation {...props} />;
  }

  return <UpsertInsuranceModalContent {...props} />;
};

const InsuranceWithAssetAllocation = (props: Props) => {
  const { insurance } = props;

  const { isLoading, data: accountPortfolioData } = useAccountPortfolioQuery({
    accountId: insurance.AccountId,
    onError() {
      toast.error(MESSAGES.GENERIC_ERROR);
    },
  });

  if (isLoading) {
    return <FullPageLoading />;
  }

  if (!accountPortfolioData) {
    return null;
  }

  return <UpsertInsuranceModalContent {...props} accountPortfolioData={accountPortfolioData} />;
};

interface ContentProps extends Props {
  accountPortfolioData?: AccountPortfolioDTO;
}

export const UpsertInsuranceModalContent = ({
  clientId,
  insurance,
  data,
  onInsuranceTypeChange,
  onClose,
  insuranceToDelete,
  setInsuranceToDelete,
  accountPortfolioData,
  isEstatePlanningWorkflow,
}: ContentProps) => {
  const queryClient = useQueryClient();

  const [isSaving, setIsSaving] = useState(false);

  const [alert, setAlert] = useState<string | null>(null);

  const initialInsuranceType = useRef(insurance.InsuranceType).current;

  const insuranceTitle =
    data.Options.InsuranceTypeOptions.find(option => option.value === insurance.InsuranceType)
      ?.label || 'Insurance';

  const Form =
    insurance.InsuranceType !== undefined ? FormsDict[insurance.InsuranceType] : React.Fragment;

  const defaultAccountPortfolio = useMemo(() => {
    return accountPortfolioDataToForm({
      accountPortfolio: accountPortfolioData,
      defaultAccountPortfolioOptions: data.accountOptions.DefaultAssetAllocation,
    });
  }, [accountPortfolioData, data.accountOptions.DefaultAssetAllocation]);

  const formBag = useForm<FormFields>({
    defaultValues: {
      ...dataToForm(insurance as InsuranceAccountDTO, data),
      accountPortfolio: defaultAccountPortfolio,
    },
    resolver: yupResolver(validation),
  });

  useEffect(() => {
    formBag.reset({
      ...dataToForm(insurance as InsuranceAccountDTO, data),
      accountPortfolio: defaultAccountPortfolio,
    });
  }, [insurance, data, formBag, defaultAccountPortfolio]);

  const { mutateAsync: saveInsurance, error: upsertInsuranceError } = useUpsertInsuranceMutation();

  const {
    mutateAsync: saveAccountPortfolio,
    error: saveAccountPortfolioError,
  } = useAccountPortfolioMutation();

  const {
    mutateAsync: saveBeneficiaries,
    error: saveBeneficiariesError,
  } = useAccountBeneficiariesMutation();

  const upsertError = upsertInsuranceError || saveAccountPortfolioError || saveBeneficiariesError;

  const onSubmit = formBag.handleSubmit(async values => {
    try {
      setIsSaving(true);

      const { AccountId } = await saveInsurance({
        ...values,
        clientId,
        AccountId: insurance.AccountId,
        AccountType: AccountType.Insurance,
        InsuranceType: insurance.InsuranceType,
      });

      const showAssetAllocationTab = getShowAssetAllocationTab({
        insurance: { ...insurance, ...values } as any,
      });

      if (showAssetAllocationTab && values.accountPortfolio) {
        await saveAccountPortfolio({
          AccountPortfolioByTaxStatuses: [
            {
              TaxStatus: null,
              HypotheticalReturn: values.accountPortfolio.hypotheticalReturn,
              AssetAllocation: values.accountPortfolio.assets,
            },
          ],
          AccountId,
        });
      }

      if (
        values.AccountBeneficiaries &&
        typeof insurance.InsuranceType === 'number' &&
        INSURANCE_TYPES_WITH_BENEFICIARIES_TAB.includes(insurance.InsuranceType)
      ) {
        await saveBeneficiaries({
          ...values.AccountBeneficiaries,
          AccountId,
        });
      }

      await Promise.all([
        queryClient.refetchQueries(queryKeys.insurance(clientId)),
        queryClient.refetchQueries([ACCOUNTS_QUERY_KEY, ACCOUNT_OPTIONS_QUERY_KEY]),
      ]);

      toast.success(
        insurance.AccountId
          ? 'Insurance policy saved successfully.'
          : 'Insurance policy added successfully.',
      );

      onClose();
    } catch (error) {
      console.error(error);

      setIsSaving(false);
    }
  });

  return (
    <Modal
      isOpen
      isHidden={insuranceToDelete !== null}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      aria={{ labelledby: 'insurance--upsert--modal-header' }}
    >
      <ModalHeader as="h2" id="insurance--upsert--modal-header">
        {initialInsuranceType !== undefined
          ? `${insurance.AccountId ? 'Edit' : 'Add'} ${insuranceTitle} Policy`
          : 'Add Insurance Policy'}
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody css="min-height: 30vh">
        {upsertError && <Alert message={upsertError.message} variant="error" mb="16" />}

        {alert && (
          <Alert
            message={alert}
            variant="info"
            onRequestClose={() => setAlert(null)}
            scrollIntoViewOptions={{ block: 'start' }}
            mt="16"
            mb="16"
          />
        )}

        {initialInsuranceType === undefined && (
          <Select
            label="Insurance Type"
            placeholder="Select Insurance Type"
            value={data.Options.InsuranceTypeOptions.find(
              ({ value }) => value === insurance.InsuranceType,
            )}
            options={data.Options.InsuranceTypeOptions}
            onChange={opt => {
              if (opt) {
                onInsuranceTypeChange(opt.value);
                setAlert(null);
              }
            }}
            isPortal
            mb="16"
          />
        )}

        <FormProvider {...formBag}>
          <form
            onSubmit={onSubmit}
            id={UPSERT_INSURANCE_FORM_ID}
            aria-label={`${insuranceTitle} form`}
          >
            <Form
              data={data}
              insurance={insurance}
              accountPortfolioData={accountPortfolioData}
              isEstatePlanningWorkflow={isEstatePlanningWorkflow}
              setAlert={setAlert}
            />
          </form>
        </FormProvider>
      </ModalBody>

      {insurance.InsuranceType !== undefined && (
        <ModalFooter>
          {insurance.AccountId && (
            <Button
              variant="errorSecondary"
              onClick={() => {
                if (insurance.AccountId) {
                  setInsuranceToDelete({ id: insurance.AccountId });
                }
              }}
              mr="auto"
            >
              Delete
            </Button>
          )}

          <Button variant="subtleInactive" onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            type="submit"
            form={UPSERT_INSURANCE_FORM_ID}
            isLoading={isSaving}
            ml="8"
          >
            {insurance.AccountId ? 'Save' : 'Add Insurance'}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};
